import type {
    TaskCheckCancelPayload,
    TaskCheckPayload,
    TaskCheckResponse,
} from '@/types/apps/task-check.type';
import type {
    ApiResponse,
    PaginatedResult,
    QueryParams,
} from '@/types/common/api.type';
import type { BaseApiClientInterface } from '@/types/service/base-api-client.interface';
import type { Creatable } from '@/types/service/creatable.interface';
import type { Deletable } from '@/types/service/deletable.interface';
import type { Indexable } from '@/types/service/indexable.interface';
import type { Showable } from '@/types/service/showable.interface';
import type { Updatable } from '@/types/service/updatable.interface';

export interface TaskCheckServiceInterface {}

type TaskCheckListResponse = ApiResponse<PaginatedResult<TaskCheckResponse>>;

export class TaskCheckService
    implements Showable, Updatable, Deletable, Creatable, Indexable
{
    public constructor(private apiClient: BaseApiClientInterface) {}

    public endpoints = {
        base: () => '/api/task-check',
        detail: (id: string) => `/api/task-check/${id}`,
        done: (id: string) => `/api/task-check/done-task-check/${id}`,
        cancel: (id: string) => `/api/task-check/cancel-task-check/${id}`,
    };

    public index(params?: QueryParams): Promise<TaskCheckListResponse> {
        const defaultParams: QueryParams = {
            // SortBy: 'desc',
            includeProperties: 'Ticket,Device',
        };

        const finalParams = {
            ...defaultParams,
            ...params,
        };

        return this.apiClient
            .fetchApi<
                PaginatedResult<TaskCheckResponse>
            >(this.endpoints.base(), finalParams)
            .then(res => {
                return {
                    ...res,
                    result: {
                        ...res.result,
                        items: res.result.items.map(item => ({
                            ...item,
                            customName: `${item.code} - ${item.name}`,
                        })),
                    },
                };
            });
    }

    public detail(
        id: string,
        params?: QueryParams
    ): Promise<ApiResponse<TaskCheckResponse>> {
        const url = this.endpoints.detail(id);

        return this.apiClient.fetchApi(url, params);
    }

    public create(
        payload: TaskCheckPayload
    ): Promise<ApiResponse<TaskCheckResponse>> {
        return this.apiClient.postApi(this.endpoints.base(), payload);
    }

    public update(
        id: string,
        payload: TaskCheckPayload
    ): Promise<ApiResponse<TaskCheckResponse>> {
        return this.apiClient.putApi(this.endpoints.detail(id), payload);
    }

    public done(id: string): Promise<ApiResponse<TaskCheckResponse>> {
        return this.apiClient.putApi(this.endpoints.done(id));
    }

    public cancel(
        id: string,
        payload: TaskCheckCancelPayload
    ): Promise<ApiResponse<TaskCheckResponse>> {
        return this.apiClient.putApi(this.endpoints.cancel(id), payload);
    }

    public destroy(
        ids: string[],
        isHardDeleted: boolean = false
    ): Promise<ApiResponse<void>> {
        const url = this.endpoints.base();

        return this.apiClient.destroyApi<void>(url, ids, isHardDeleted);
    }
}
